import { Box, Button, Chip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import type { AvailableOption } from '@/types';
import { formatPriceDiff } from '@/utils/price.utils';
import { composeCaption } from '@/utils/options.utils';
import { DoorGlyph } from './doorGlyphs';
import { refineGlyph } from './techGlyphs';
import { ProductActions } from './ProductActions';

interface OptionSolutionCardProps {
  option: AvailableOption;
  /** 'add' = add-on per-posizione (AGGIUNGI/ELIMINA); 'select' = scelta-singola (SELEZIONA/SELEZIONATO). */
  mode: 'add' | 'select';
  /** Frase per-posizione dal BE ("Seleziono le porte scorrevoli nella posizione:"), null se non mappata. */
  caption: string | null;
  selected: boolean;
  isBusy: boolean;
  onToggle: (option: AvailableOption) => void;
}

const CardRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  // Legacy OptionCard: fondo grigio chiaro #F0EFEE, bordo grigio #BEBCBB (verde quando selezionata).
  backgroundColor: '#F0EFEE',
  border: `${selected ? 2 : 1}px solid`,
  borderColor: selected ? theme.palette.secondary.main : '#BEBCBB',
  borderRadius: theme.shape.borderRadius,
  transition: 'border-color 200ms ease',
}));

function optionName(option: AvailableOption): string {
  return option.description ?? option.productCode ?? `Opzione ${option.optionId}`;
}

/**
 * Card "SOLUZIONI AGGIUNTIVE/ALTERNATIVE" (colonna centrale) per una variante d'opzione. Riproduce il
 * pattern legacy: titolo per-posizione, pulsante AGGIUNGI ⇄ AGGIUNTO/ELIMINA, +prezzo, badge
 * "Consigliato dall'architetto", +prezzo, e i segnaposto Foto/Scheda Tecnica (asset non ancora
 * disponibili → disabilitati). La tagline/warning del substep sono a livello di colonna (ColumnIntro),
 * non nella card. Il render prodotto (elevazione porta) è demandato a un asset dedicato (TODO): il
 * layer di planimetria NON è adatto come render di card.
 */
export function OptionSolutionCard({
  option,
  mode,
  caption,
  selected,
  isBusy,
  onToggle,
}: OptionSolutionCardProps) {
  const name = optionName(option);
  // Caption composta (mount "a soffitto/parete" + stanza/posizione) dal template BE. Solo per add-on.
  const composed = mode === 'add' ? composeCaption(caption, option) : null;

  return (
    <CardRoot selected={selected}>
      {/* Banner "Consigliato dall'architetto" (giallo, serif corsivo con stella) come nel legacy. */}
      {option.isSuggested && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mx: -2,
            mt: -2,
            px: 2,
            py: 0.75,
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
          }}
        >
          <StarIcon fontSize="small" aria-hidden="true" />
          <Typography
            component="span"
            sx={{ fontFamily: '"miller", serif', fontStyle: 'italic', fontSize: 13, lineHeight: 1.2 }}
          >
            Consigliato dall&apos;architetto
          </Typography>
        </Box>
      )}

      {/* Glifo (porta/tecnologia), raffinato coi dati opzione (mount/stanza/pacchetto). Assente → niente. */}
      {option.glyph && <DoorGlyph name={refineGlyph(option)} height={72} label={name} />}

      <Box>
        {composed ? (
          <Typography variant="body2" color="text.primary">
            {composed.lead}{' '}
            <Box component="span" sx={{ color: 'secondary.dark', fontWeight: 600 }}>
              {composed.emphasis}
            </Box>
          </Typography>
        ) : (
          // Senza caption (es. SmartHome: le "posizioni" sono pacchetti) → solo il nome, niente "P-n".
          <Typography variant="body1" fontWeight={500}>
            {name}
          </Typography>
        )}
      </Box>

      <Typography variant="body2" color="primary.main" fontWeight={600}>
        {formatPriceDiff(option.price)}
      </Typography>

      {/* Foto / Scheda Tecnica reali (asset di catalogo dal BE); appaiono solo se presenti. */}
      <ProductActions product={option.product} name={name} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        {mode === 'add' ? (
          selected ? (
            <>
              <Chip
                size="small"
                color="secondary"
                icon={<CheckCircleIcon fontSize="small" />}
                label="Aggiunto"
                sx={{ fontWeight: 500 }}
              />
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<DeleteOutlineIcon />}
                disabled={isBusy}
                onClick={() => onToggle(option)}
              >
                Elimina
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              disabled={isBusy}
              onClick={() => onToggle(option)}
            >
              Aggiungi
            </Button>
          )
        ) : (
          <Button
            variant={selected ? 'outlined' : 'contained'}
            color="secondary"
            startIcon={selected ? <CheckCircleIcon /> : undefined}
            disabled={isBusy || selected}
            onClick={() => onToggle(option)}
          >
            {selected ? 'Selezionato' : 'Seleziona'}
          </Button>
        )}
      </Box>
    </CardRoot>
  );
}
